import React from 'react';
import queryString from 'query-string';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import moment from 'moment';
import { connect } from 'react-redux';
import i18next from 'i18next';

import { withTheme } from '@material-ui/core/styles';
import { getFormat, twoDigits, makeGraph } from 'lib/commonLib';
import { cwGraph } from 'lib/api/time';

import CurrentStatus from 'components/atoms/CurrentStatus';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import ContentsWrap from 'components/molecules/ContentsWrap';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const graphList = [
  {
    title: i18next.t('CW통계 그래프'),
    data: {
      bar: ['PARAM_CNT'],
      line: [
        'ADVRTS_AMT',
        'PAR_EPRS_CNT',
        'CLICK_CNT',
        'CLICK_RATE',
        'BDGT_AMT',
      ],
    },
    yAxisData: {
      left: {
        ADVRTS_AMT: true,
        PAR_EPRS_CNT: true,
        CLICK_CNT: true,
        CLICK_RATE: { isPercent: true },
        BDGT_AMT: true,
        individually: true,
      },
      right: { PARAM_CNT: true },
    },
    customizeData: {
      BDGT_AMT: {
        width: 70,
        color: '#FFBB00',
      },
      CLICK_RATE: {
        width: 60,
        color: '#FF0000',
        isPercent: true,
        tooltipFormat: '0.000',
      },
      CLICK_CNT: {
        width: 60,
        color: '#2F9D27',
      },
      PAR_EPRS_CNT: {
        width: 70,
        color: '#00D8FF',
      },
      ADVRTS_AMT: {
        width: 80,
        color: '#0100FF',
      },
    },
    referenceLine: {
      ADVRTS_AMT: {
        type: 'average',
        label: i18next.t('지출금액') + ' ' + i18next.t('평균선'),
        visibleValue: true,
        color: '#0100FF',
      },
      BDGT_AMT: {
        type: 'average',
        label: i18next.t('허용예산금액') + ' ' + i18next.t('평균선'),
        visibleValue: true,
        color: '#FFBB00',
      },
      CLICK_RATE: {
        type: 'average',
        label: i18next.t('클릭률') + ' ' + i18next.t('평균선'),
        visibleValue: true,
        color: '#FF0000',
      },
      CLICK_CNT: {
        type: 'average',
        label: i18next.t('클릭수') + ' ' + i18next.t('평균선'),
        visibleValue: true,
        color: '#2F9D27',
      },
      PAR_EPRS_CNT: {
        type: 'average',
        label: i18next.t('구좌노출수') + ' ' + i18next.t('평균선'),
        visibleValue: true,
        color: '#00D8FF',
      },
    },
  },
];

class CwAnalysisGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphData: [],
      infoMessage: '',
      loading: false,
      error: false,
      noData: false,
    };
  }

  getData = () => {
    const { params, locale, t, location, handleClickSnackbar } = this.props;
    const { uri } = queryString.parse(location.search);
    const { sDate, eDate } = params;
    const format = getFormat(locale);
    this.setState({
      ...this.state,
      loading: true,
      error: false,
      noData: false,
      infoMessage: t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      }),
    });
    cwGraph({ ...params, uri })
      .then((response) => {
        const { data } = response.data;

        if (data.length === 0) {
          this.setState({
            ...this.state,
            loading: false,
            error: false,
            noData: true,
          });
        } else if (typeof data !== 'undefined') {
          this.setState({
            ...this.state,
            primitiveData: data,
            loading: false,
            error: false,
            noData: false,
          });

          this.handleChangeGraph(graphList[0], 0)();
        }
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
        console.log(error);
        this.setState({
          ...this.state,
          loading: false,
          error: true,
          noData: false,
        });
      });
  };

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    const { params: prevParams } = prevProps;
    const { params } = this.props;

    if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
      this.getData();
    }
  }

  /**
   * 새로고침
   */
  handleRefesh = () => {
    this.getData();
  };

  // 월 일 or 일 반환
  makeLabel = (onlyDay) => (originalDate) => {
    const { t, theme } = this.props;
    const dateObj = moment(originalDate, 'YYYYMMDD');
    const month = twoDigits(dateObj.month() + 1);
    const date = dateObj.date();
    const day = dateObj.day();
    let color = '';

    if (day === 0) {
      color = theme.palette.common.red;
    } else if (day === 6) {
      color = theme.palette.common.blue;
    }

    return onlyDay ? (
      `${twoDigits(date)}${t('일')}`
    ) : (
      <span style={{ color }}>{`${t(month + '월')} ${twoDigits(date)}${t(
        '일'
      )}`}</span>
    );
  };

  xAxisTick = (tickProps) => {
    const { theme } = this.props;
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const dateObj = moment(value, 'YYYYMMDD');
    const day = dateObj.day();

    const dayStr = this.makeLabel(true)(value);
    let fill = '';
    //Sunday as 0 and Saturday as 6.
    if (day === 0) {
      fill = theme.palette.common.red;
    } else if (day === 6) {
      fill = theme.palette.common.blue;
    }

    return (
      <text x={x} y={y + 10} fill={fill} textAnchor="middle">
        {dayStr}
      </text>
    );
  };

  /**
   * 버튼 클릭하여 그래프 데이터 변경
   * graphData json, index
   */
  handleChangeGraph =
    (
      {
        title,
        data,
        yAxisData,
        customizeData: customize,
        isPercent = false,
        referenceLine,
      },
      index
    ) =>
    (e) => {
      const { primitiveData } = this.state;

      if (typeof data !== 'undefined') {
        const { graphData, dataKeys, customizeData, keyLabel, referenceData } =
          makeGraph({ data, customize, primitiveData, referenceLine });

        const result = (
          <MultiplexChart
            xAxisTick={this.xAxisTick}
            makeLabel={this.makeLabel(false)}
            data={graphData}
            dataKeys={dataKeys}
            title={title}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={null}
            yAxisData={yAxisData}
            legend
            isPercent={isPercent}
            key={title}
            referenceLine={referenceData}
          />
        );

        this.setState({
          ...this.state,
          curGraphComponent: result,
          selectedIdx: index,
        });
      }
    };

  render() {
    const { handleRefesh } = this;
    const { t } = this.props;
    const { infoMessage, curGraphComponent, loading, error, noData } =
      this.state;

    return (
      <SwitchingArea
        title={
          <>
            <InfoTitleArea
              title={t('CW통계 그래프')}
              tooltipTitle={infoMessage}
              iconType="fainfoCircle"
              iconColor="graph"
            />
          </>
        }
        handleRefesh={handleRefesh}
      >
        {loading || noData || error ? (
          <CurrentStatus
            type={loading ? 'loading' : noData ? 'noData' : error && 'error'}
          />
        ) : (
          <>
            <p style={{ margin: '0 0 5px 0' }}>
              장바구니 모수는 소진이 발생된 모수의 Count 입니다.
            </p>
            <ContentsWrap contentsCount={1}>{curGraphComponent}</ContentsWrap>
          </>
        )}
      </SwitchingArea>
    );
  }
}

export default withRouter(
  withTranslation()(
    connect(
      ({ locale }) => ({
        locale: locale.locale,
      }),
      null
    )(withTheme(CwAnalysisGraph))
  )
);
